import React from 'react';
import { KpiCard } from '../../../components/app2';

/**
 * Tarjetas-KPI del panel de empresa (rediseño jul-2026).
 *
 * El cálculo es el mismo de siempre; solo cambia cómo se pinta. El indicador
 * secundario de cada tarjeta (tasa de descarga, porcentaje procesado…) ya
 * existía antes del rediseño y se conserva: no es un "delta contra el periodo
 * anterior" —eso no lo hay en el backend—, es un dato que la pantalla ya
 * calculaba con lo que tiene a mano.
 */
const PayrollStatsCards = ({ companyData, periodos = [] }) => {
  const calculateStats = () => {
    if (!companyData?.employees) {
      return {
        totalEmployees: 0, processedPayrolls: 0, totalReceipts: 0,
        ultimoPeriodo: null, lastSync: 'N/A',
      };
    }

    const employees = companyData?.employees || [];
    const totalEmployees = employees?.length;

    // Periodos de nómina del mes en curso y total de recibos, con datos reales.
    // Antes se contaba `last_payroll_date` y `receipt_status`, campos que el
    // backend nunca envía: ambas tarjetas mostraban 0 permanentemente.
    const hoy = new Date();
    const processedPayrolls = periodos.filter((p) => {
      if (!p?.periodEnd) return false;
      const [y, m] = String(p.periodEnd).slice(0, 10).split('-').map(Number);
      return y === hoy.getFullYear() && m - 1 === hoy.getMonth();
    }).length;

    const totalReceipts = periodos.reduce((n, p) => n + (p?.receipts || 0), 0);
    const ultimoPeriodo = periodos[0]?.periodEnd || null;

    const lastSync = companyData?.updated_at
      ? new Date(companyData?.updated_at)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
      : new Date()?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

    return { totalEmployees, processedPayrolls, totalReceipts, ultimoPeriodo, lastSync };
  };

  const stats = calculateStats();
  const fechaCorta = (iso) => {
    if (!iso) return 'Sin recibos';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  };

  const cards = [
    {
      icon: 'group',
      value: String(stats.totalEmployees ?? 0),
      label: 'Total empleados',
      sub: 'Empleados activos',
      note: stats.totalEmployees > 0 ? `+${stats.totalEmployees}` : '0',
      noteIcon: stats.totalEmployees > 0 ? 'trending_up' : 'remove',
      noteTone: stats.totalEmployees > 0 ? 'ok' : 'muted',
    },
    {
      icon: 'description',
      value: String(stats.processedPayrolls ?? 0),
      label: 'Nóminas procesadas',
      sub: 'Este mes',
      note: stats.processedPayrolls > 0 ? `${stats.processedPayrolls}` : '0',
      noteIcon: stats.processedPayrolls > 0 ? 'trending_up' : 'remove',
      noteTone: stats.processedPayrolls > 0 ? 'ok' : 'muted',
    },
    {
      icon: 'receipt_long',
      value: stats.totalReceipts.toLocaleString('es-MX'),
      label: 'Recibos en el sistema',
      sub: `Último periodo: ${fechaCorta(stats.ultimoPeriodo)}`,
      note: `${periodos.length}`,
      noteIcon: 'event_repeat',
      noteTone: 'muted',
    },
    {
      icon: 'sync',
      value: stats.lastSync || 'N/A',
      label: 'Última sincronización',
      sub: 'SAT actualizado',
      note: 'Hoy',
      noteIcon: 'check_circle',
      noteTone: 'ok',
    },
  ];

  return (
    <div className="a2-grid-kpi" style={{ marginBottom: 16 }}>
      {cards.map((c) => <KpiCard key={c.label} {...c} />)}
    </div>
  );
};

export default PayrollStatsCards;
